// Nursing notes and ward handover (docs/plans/phase2-ward-rounds-nursing.md §4.3).

import { err, ok, type Result } from '../result.ts';
import { addDays, eatDayOf, eatTimeOf, type CalendarDay } from '../time.ts';

export const HANDOVER_SHIFTS = ['day', 'night'] as const;
export type HandoverShift = (typeof HANDOVER_SHIFTS)[number];

/**
 * When each shift starts, East Africa Time. TODO(business): plan W4, provisional
 * 07:00 to 19:00 and 19:00 to 07:00 until each facility's rota is configurable.
 */
export const SHIFT_START_HOURS: Readonly<Record<HandoverShift, number>> = { day: 7, night: 19 };

export const NURSING_NOTE_MAX = 4000;
export const HANDOVER_TEXT_MAX = 8000;

export interface ShiftOf {
  shift: HandoverShift;
  /** The EAT day the shift started on: 02:00 belongs to the previous day's night. */
  shiftDate: CalendarDay;
}

export function shiftOf(instant: Date): ShiftOf {
  const day = eatDayOf(instant);
  const { hour } = eatTimeOf(instant);
  if (hour >= SHIFT_START_HOURS.day && hour < SHIFT_START_HOURS.night) {
    return { shift: 'day', shiftDate: day };
  }
  return {
    shift: 'night',
    shiftDate: hour >= SHIFT_START_HOURS.night ? day : addDays(day, -1),
  };
}

export type NursingTextErrorCode = 'note.empty' | 'note.too_long';

export const NURSING_TEXT_ERRORS: Record<NursingTextErrorCode, string> = {
  'note.empty': 'Write the note before saving it',
  'note.too_long': 'The note is too long. Shorten it or split it into two notes',
};

/** A note as it is stored: trimmed, not empty, within the limit. */
export function checkNursingText(text: string, max: number): Result<string, NursingTextErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('note.empty');
  if (trimmed.length > max) return err('note.too_long');
  return ok(trimmed);
}
